<!--
OTA升级管理组件
被主导航调用
权限点：device.manage
简要说明：用于固件版本管理和设备升级，所有操作按钮需加权限判断
-->
<template>
  <div class="ota-management">
    <!-- 固件版本管理 -->
    <div class="firmware-section">
      <el-card class="section-card">
        <template #header>
          <div class="card-header">
            <span>固件版本管理</span>
            <div class="header-actions">
              <el-button
                type="primary"
                @click="showUploadDialog = true"
                v-if="hasPermission('ota.manage')"
              >
                <el-icon><Upload /></el-icon>
                上传固件
              </el-button>
              <el-button
                link
                @click="refreshFirmware"
                v-if="hasPermission('ota.manage')"
              >
                <el-icon><Refresh /></el-icon>
                刷新
              </el-button>
            </div>
          </div>
        </template>

        <el-table
          ref="firmwareTableRef"
          :data="firmwareList"
          :style="{ width: '100%' }"
          highlight-current-row
          @current-change="handleFirmwareSelectionChange"
          :row-class-name="getFirmwareRowClass"
        >
          <el-table-column label="选择" width="80" align="center">
            <template #default="scope">
              <el-radio
                :model-value="selectedFirmwareId ?? undefined"
                :label="Number(scope.row.id)"
                @change="() => handleFirmwareSelectionChange(scope.row)"
              />
            </template>
          </el-table-column>
          <el-table-column prop="version" label="版本号" width="120" />
          <el-table-column prop="description" label="版本描述" />
          <el-table-column prop="size" label="文件大小" width="100" />
          <el-table-column prop="uploadTime" label="上传时间" width="160" />
          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag
                :type="scope.row.status === 'latest' ? 'success' : 'info'"
              >
                {{ scope.row.status === "latest" ? "当前版本" : "历史版本" }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200">
            <template #default="scope">
              <div class="action-buttons">
                <el-button
                  type="primary"
                  size="small"
                  @click="setActiveFirmware(scope.row)"
                  v-if="hasPermission('ota.manage')"
                >
                  {{ scope.row.status === "latest" ? "设为历史" : "设为当前" }}
                </el-button>
                <el-button
                  type="danger"
                  size="small"
                  @click="deleteFirmware(scope.row)"
                  v-if="hasPermission('ota.manage')"
                >
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <!-- 音频资源管理 -->
    <div class="audio-section">
      <el-card class="section-card">
        <template #header>
          <div class="card-header">
            <span>音频资源管理</span>
            <div class="header-actions" v-if="hasPermission('ota.manage')">
              <el-button type="primary" @click="openAudioUploadDialog()">
                <el-icon><Upload /></el-icon>
                上传音频
              </el-button>
              <el-button link @click="fetchAudioAssets">
                <el-icon><Refresh /></el-icon>
                刷新
              </el-button>
            </div>
          </div>
        </template>

        <el-alert
          type="info"
          show-icon
          class="audio-tip"
          title="仅支持 .mp3 文件，大小 ≤ 300KB，文件名为大小写字母加数字组成。"
        />

        <el-table
          :data="audioTableData"
          :style="{ width: '100%', marginTop: '16px' }"
          v-loading="audioLoading"
          empty-text="暂无音频资源"
        >
          <el-table-column prop="label" label="音频类型" width="140" />
          <el-table-column label="文件信息">
            <template #default="scope">
              <div v-if="scope.row.asset">
                <div class="audio-meta">
                  <span class="audio-file">{{ scope.row.asset.filename }}</span>
                  <el-tag size="small">{{
                    formatFileSize(scope.row.asset.file_size)
                  }}</el-tag>
                </div>
                <div class="audio-meta">
                  <span>MD5：{{ scope.row.asset.file_md5 }}</span>
                  <span
                    >上传：{{
                      formatUploadTime(scope.row.asset.uploaded_at)
                    }}</span
                  >
                </div>
                <el-link
                  type="primary"
                  :href="scope.row.asset.download_url"
                  target="_blank"
                  v-if="scope.row.asset.download_url"
                >
                  下载链接
                </el-link>
              </div>
              <span v-else class="audio-empty">未上传</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="160" align="center">
            <template #default="scope">
              <el-button
                type="primary"
                text
                size="small"
                @click="openAudioUploadDialog(scope.row.value)"
                v-if="hasPermission('ota.manage')"
              >
                {{ scope.row.asset ? "替换" : "上传" }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <!-- 设备升级状态 -->
    <div class="device-upgrade-section">
      <el-card class="section-card">
        <template #header>
          <div class="card-header">
            <span>设备升级状态</span>
            <div class="header-filters">
              <el-select
                v-model="statusFilter"
                placeholder="筛选状态"
                style="width: 120px"
                @change="handleFilterChanged"
              >
                <el-option label="全部" value="" />
                <el-option label="最新版本" value="latest" />
                <el-option label="待升级" value="pending" />
                <el-option label="需要升级" value="outdated" />
                <el-option label="升级中" value="upgrading" />
                <el-option label="升级失败" value="failed" />
              </el-select>
              <el-input
                v-model="searchKeyword"
                placeholder="搜索设备"
                style="width: 200px"
                clearable
                @input="handleKeywordInput"
                @clear="handleKeywordCleared"
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
              <el-button
                type="primary"
                @click="handleTargetedUpgrade"
                :disabled="!canStartTargetedUpgrade"
                v-if="hasPermission('ota.manage')"
              >
                对选中设备升级到所选版本
              </el-button>
            </div>
          </div>
        </template>

        <el-table
          ref="deviceTableRef"
          :data="filteredDeviceList"
          :style="{ width: '100%' }"
          @selection-change="handleDeviceSelectionChange"
        >
          <el-table-column type="selection" width="55" />
          <el-table-column prop="device_id" label="设备编号" width="180" />
          <el-table-column prop="deviceName" label="设备名称" width="160" />
          <el-table-column prop="currentVersion" label="当前版本" width="120" />
          <el-table-column label="目标版本" width="120">
            <template #default="scope">
              {{ getDeviceTargetVersion(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column prop="upgradeStatus" label="升级状态" width="120">
            <template #default="scope">
              <el-tag :type="getStatusType(scope.row.upgradeStatus)">
                {{ getStatusText(scope.row.upgradeStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="upgradeProgress" label="升级进度" width="120">
            <template #default="scope">
              <el-progress
                v-if="scope.row.upgradeStatus === 'upgrading'"
                :percentage="scope.row.upgradeProgress"
                :stroke-width="8"
                :show-text="false"
              />
              <span v-else>{{ scope.row.upgradeProgress }}%</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="lastUpgradeTime"
            label="最后升级时间"
            width="160"
          />
          <el-table-column label="操作" width="200">
            <template #default="scope">
              <div class="action-buttons">
                <el-button
                  type="primary"
                  size="small"
                  :disabled="scope.row.upgradeStatus === 'upgrading'"
                  @click="upgradeDevice(scope.row)"
                  v-if="hasPermission('ota.manage')"
                >
                  升级
                </el-button>
                <el-button
                  type="warning"
                  size="small"
                  :disabled="scope.row.upgradeStatus !== 'upgrading'"
                  @click="cancelUpgrade(scope.row)"
                  v-if="hasPermission('ota.manage')"
                >
                  取消
                </el-button>
                <el-button
                  type="info"
                  size="small"
                  @click="viewUpgradeLog(scope.row)"
                >
                  日志
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="table-footer">
          <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="deviceList.length"
            layout="total, sizes, prev, pager, next, jumper"
          />
        </div>
      </el-card>
    </div>

    <!-- 上传固件对话框 -->
    <el-dialog
      v-model="showUploadDialog"
      title="上传固件"
      width="500px"
      @close="resetUploadForm"
    >
      <el-form
        :model="uploadForm"
        :rules="uploadRules"
        ref="uploadFormRef"
        label-width="80px"
        label-position="left"
      >
        <el-form-item label="版本号" prop="version">
          <el-input
            v-model="uploadForm.version"
            placeholder="请输入版本号，如：v1.2.3"
          />
        </el-form-item>
        <el-form-item label="版本描述" prop="description">
          <el-input
            v-model="uploadForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入版本描述"
          />
        </el-form-item>
        <el-form-item label="固件文件" prop="file">
          <el-upload
            class="upload-demo"
            drag
            :auto-upload="false"
            :on-change="handleFileChange"
            :file-list="fileList"
            accept=".bin,.hex,.fw"
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              将文件拖到此处，或<em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                只能上传 .bin/.hex/.fw 文件，且不超过 50MB
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="handleCancelUpload">取消</el-button>
          <el-button
            type="primary"
            @click="uploadFirmware"
            v-if="hasPermission('ota.manage')"
            >上传</el-button
          >
        </div>
      </template>
    </el-dialog>

    <!-- 上传音频对话框 -->
    <el-dialog
      v-model="showAudioUploadDialog"
      title="上传音频资源"
      width="460px"
      @close="resetAudioUploadForm"
    >
      <el-form
        :model="audioUploadForm"
        label-width="90px"
        label-position="left"
      >
        <el-form-item label="音频类型">
          <el-select
            v-model="audioUploadForm.audioType"
            placeholder="请选择音频类型"
          >
            <el-option
              v-for="item in audioTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="音频文件">
          <el-upload
            class="upload-audio"
            drag
            accept=".mp3"
            :auto-upload="false"
            :file-list="audioFileList"
            :on-change="handleAudioFileChange"
            :on-remove="handleAudioFileRemove"
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              拖拽到此处，或<em>点击选择 mp3 文件</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                文件名需 ASCII 字符，大小 ≤ 300KB，仅支持 .mp3
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="cancelAudioUpload">取消</el-button>
          <el-button
            type="primary"
            @click="uploadAudioAsset"
            :loading="audioUploadLoading"
          >
            上传
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";
import { useStore } from "vuex";
import { DataService } from "../services/dataService";
import { logger } from "../utils/logger";

const { Upload, UploadFilled, Refresh, Search } =
  ElementPlusIconsVue as unknown as Record<string, any>;

const store = useStore();
const hasPermission = (perm: string) => store.getters.hasPermission(perm);

const ASCII_FILENAME_REGEX = /^[\x21-\x7E]+$/;

// 版本号比较：返回 1(a>b)、-1(a<b)、0(a==b)
const normalizeVersion = (v: string): number[] => {
  if (!v) return [];
  const core = v.trim().replace(/^v/i, "");
  return core.split(".").map((p) => Number(p) || 0);
};

const compareVersion = (a: string, b: string): number => {
  const pa = normalizeVersion(a);
  const pb = normalizeVersion(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const va = pa[i] ?? 0;
    const vb = pb[i] ?? 0;
    if (va > vb) return 1;
    if (va < vb) return -1;
  }
  return 0;
};

// 本文件为 OTA 管理页面，主要用于 OTA 设备列表展示。2024-07-24：统一接口字段，所有设备列表统一用 data.items。
// 组件数据
const showUploadDialog = ref(false);
const statusFilter = ref("");
const searchKeyword = ref("");
const currentPage = ref(1);
const pageSize = ref(20);

// 组件状态
const loading = ref(false);
const firmwareList = ref<any[]>([]);
const selectedFirmwareId = ref<number | null>(null);
const firmwareTableRef = ref();

// 设备列表
const deviceList = ref<any[]>([]);
const selectedDevices = ref<any[]>([]);
const deviceTableRef = ref<any>(null);
const keywordDebounceTimer = ref<number | null>(null);
const KEYWORD_DEBOUNCE_MS = 300;
let otaPollingTimer: number | null = null;

const cancelKeywordDebounce = () => {
  if (keywordDebounceTimer.value !== null) {
    window.clearTimeout(keywordDebounceTimer.value);
    keywordDebounceTimer.value = null;
  }
};

const clearDeviceSelection = () => {
  selectedDevices.value = [];
  void nextTick(() => {
    if (
      deviceTableRef.value &&
      typeof deviceTableRef.value.clearSelection === "function"
    ) {
      deviceTableRef.value.clearSelection();
    }
  });
};

const handleFilterChanged = () => {
  cancelKeywordDebounce();
  clearDeviceSelection();
  fetchOTAData();
};

const handleKeywordInput = () => {
  cancelKeywordDebounce();
  clearDeviceSelection();
  keywordDebounceTimer.value = window.setTimeout(() => {
    keywordDebounceTimer.value = null;
    fetchOTAData();
  }, KEYWORD_DEBOUNCE_MS);
};

const handleKeywordCleared = () => {
  cancelKeywordDebounce();
  clearDeviceSelection();
  fetchOTAData();
};

// 音频资源管理
const audioLoading = ref(false);
const audioAssets = ref<Record<string, any>>({ alarm: null, wait: null });
const audioTypeOptions = [
  { label: "告警音 (alarm)", value: "alarm" },
  { label: "等待音 (wait)", value: "wait" },
];
const audioTableData = computed(() =>
  audioTypeOptions.map((item) => ({
    ...item,
    asset: audioAssets.value[item.value],
  }))
);
const showAudioUploadDialog = ref(false);
const audioUploadLoading = ref(false);
const audioUploadForm = ref<{ audioType: string; file: File | null }>({
  audioType: "alarm",
  file: null,
});
const audioFileList = ref<any[]>([]);

const formatFileSize = (size?: number | null) => {
  if (!size || size <= 0) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const formatUploadTime = (time?: string | null) => {
  if (!time) return "-";
  try {
    return new Date(time).toLocaleString();
  } catch {
    return "-";
  }
};

const startOtaPolling = () => {
  if (otaPollingTimer !== null) {
    return;
  }
  otaPollingTimer = window.setInterval(() => {
    fetchOTAData();
  }, 5000);
};

const stopOtaPolling = () => {
  if (otaPollingTimer !== null) {
    clearInterval(otaPollingTimer);
    otaPollingTimer = null;
  }
};

// 获取OTA数据
const fetchOTAData = async () => {
  loading.value = true;
  try {
    // 并行获取固件列表和设备列表
    const [firmwareResponse, deviceResponse] = await Promise.all([
      DataService.getOTAFirmwareList(),
      DataService.getOTADeviceList({
        status: statusFilter.value,
        keyword: searchKeyword.value,
      }),
    ]);

    // 更新固件列表
    if (
      firmwareResponse &&
      typeof firmwareResponse === "object" &&
      "data" in firmwareResponse
    ) {
      const resp = firmwareResponse as { data: any };
      if (resp.data && resp.data.code === 0) {
        firmwareList.value = resp.data.data;
      }
    }

    // 更新设备列表（后端字段转换为前端驼峰命名并归一状态值）
    let items: any[] = [];
    if (
      deviceResponse &&
      typeof deviceResponse === "object" &&
      "data" in deviceResponse
    ) {
      const resp = deviceResponse as { data: any };
      if (resp.data && resp.data.code === 0) {
        const responseData = resp.data.data;
        if (Array.isArray(responseData)) {
          items = responseData.map((d: Record<string, any>) => {
            const rawStatus = d.upgrade_status || "";
            const normalizedStatus =
              rawStatus === "up_to_date" ? "latest" : rawStatus || "outdated";
            const finalStatus =
              normalizedStatus === "cancelled" ? "outdated" : normalizedStatus;

            return {
              device_id: d.device_id,
              deviceName: d.device_name,
              currentVersion: d.current_version,
              targetVersion: d.target_version,
              _backendTargetVersion: d.target_version,
              upgradeStatus: finalStatus,
              lastBackendStatus: normalizedStatus,
              upgradeProgress: d.upgrade_progress ?? 0,
              lastUpgradeTime: d.last_upgrade_time,
            };
          });
        }
      }
    }
    deviceList.value = items;
    clearDeviceSelection();

    const hasRunningTask = items.some((device) =>
      ["upgrading", "pending"].includes(device.upgradeStatus)
    );
    if (hasRunningTask) {
      startOtaPolling();
    } else {
      stopOtaPolling();
    }
  } catch (error) {
    logger.error("获取OTA数据失败:", error);
    ElMessage.error("获取数据失败，请稍后重试");
  } finally {
    loading.value = false;
  }
};

const fetchAudioAssets = async () => {
  audioLoading.value = true;
  try {
    const response = await DataService.getOTAAudioAssets();
    if (response && response.data && response.data.code === 0) {
      audioAssets.value = response.data.data || { alarm: null, wait: null };
    } else {
      audioAssets.value = { alarm: null, wait: null };
      ElMessage.error(response?.data?.message || "获取音频资源失败");
    }
  } catch (error) {
    logger.error("获取音频资源失败:", error);
    ElMessage.error("获取音频资源失败，请稍后重试");
    audioAssets.value = { alarm: null, wait: null };
  } finally {
    audioLoading.value = false;
  }
};

// 上传表单
const uploadForm = ref({
  version: "",
  description: "",
  file: null,
});

const uploadRules = {
  version: [{ required: true, message: "请输入版本号", trigger: "blur" }],
  description: [{ required: true, message: "请输入版本描述", trigger: "blur" }],
};

const fileList = ref([]);
const uploadFormRef = ref();

// 过滤设备列表
const filteredDeviceList = computed(() => {
  let filtered = deviceList.value;

  if (statusFilter.value) {
    filtered = filtered.filter(
      (device) => device.upgradeStatus === statusFilter.value
    );
  }

  if (searchKeyword.value) {
    filtered = filtered.filter(
      (device) =>
        device.deviceName.includes(searchKeyword.value) ||
        device.device_id.includes(searchKeyword.value)
    );
  }

  return filtered;
});

const selectedFirmware = computed(() => {
  if (selectedFirmwareId.value === null) {
    return null;
  }
  return (
    firmwareList.value.find(
      (fw) => Number(fw.id) === Number(selectedFirmwareId.value)
    ) || null
  );
});

const canStartTargetedUpgrade = computed(
  () => selectedFirmware.value !== null && selectedDevices.value.length > 0
);

const handleFirmwareSelectionChange = (row: any) => {
  if (!row) {
    selectedFirmwareId.value = null;
    return;
  }
  selectedFirmwareId.value = Number(row.id);
};

const getFirmwareRowClass = ({ row }: { row: any }) => {
  return Number(row.id) === Number(selectedFirmwareId.value)
    ? "selected-firmware-row"
    : "";
};

const handleDeviceSelectionChange = (selection: any[]) => {
  selectedDevices.value = selection;
};

const selectedDeviceIdSet = computed(
  () => new Set(selectedDevices.value.map((d: any) => d.device_id))
);

const getDeviceTargetVersion = (device: any) => {
  const fw = selectedFirmware.value;
  if (fw && fw.version && selectedDeviceIdSet.value.has(device.device_id)) {
    return fw.version;
  }
  if (device._backendTargetVersion !== undefined) {
    return device._backendTargetVersion;
  }
  return device.targetVersion;
};

const startUpgradeForDevices = async (
  deviceIds: string[],
  options: { resetSelection?: boolean } = {}
) => {
  if (!selectedFirmware.value) {
    ElMessage.warning("请先选择目标固件版本");
    return false;
  }
  if (deviceIds.length === 0) {
    ElMessage.warning("请至少选择一台设备");
    return false;
  }

  loading.value = true;
  try {
    const payload = {
      firmwareId: selectedFirmwareId.value as number,
      device_ids: deviceIds,
    };

    const response = await DataService.startOTADeviceUpgrade(payload);

    if (response && response.data && response.data.code === 0) {
      const respData = response.data.data || {};
      const restartFailed: any[] = respData.restart_failed || [];
      const restartSuccess: string[] = respData.restart_success || [];
      const message =
        response.data.message ||
        (restartFailed.length > 0
          ? `升级任务已创建，成功重启 ${restartSuccess.length} 台，${restartFailed.length} 台失败`
          : `升级任务已创建，已重启 ${restartSuccess.length} 台设备`);
      if (restartFailed.length > 0) {
        ElMessage.warning(message);
      } else {
        ElMessage.success(message);
      }

      if (options.resetSelection) {
        clearDeviceSelection();
      }

      await fetchOTAData();
      return true;
    }

    ElMessage.error(response?.data?.message || "创建升级任务失败");
    return false;
  } catch (error) {
    logger.error("创建定向升级任务失败", error);
    ElMessage.error("创建升级任务失败，请稍后重试");
    return false;
  } finally {
    loading.value = false;
  }
};

const handleTargetedUpgrade = async () => {
  if (!selectedFirmware.value) {
    ElMessage.warning("请先选择目标固件版本");
    return;
  }
  if (selectedDevices.value.length === 0) {
    ElMessage.warning("请勾选至少一台设备");
    return;
  }

  const firmwareVersion = selectedFirmware.value.version;
  const deviceCount = selectedDevices.value.length;

  const hasDowngrade = selectedDevices.value.some(
    (device: any) => compareVersion(device.currentVersion, firmwareVersion) > 0
  );
  if (hasDowngrade) {
    ElMessage.warning(
      `批量升级仅支持从低版本升级到高版本。如需将更高版本降级到 ${firmwareVersion}，请在设备行使用“升级”按钮单独操作。`
    );
    return;
  }

  const confirmMessage = `确定将 ${deviceCount} 台设备升级到版本 ${firmwareVersion} 吗？`;

  try {
    await ElMessageBox.confirm(confirmMessage, "确认升级", {
      type: "warning",
      confirmButtonText: "确定",
      cancelButtonText: "取消",
    });
    await startUpgradeForDevices(
      selectedDevices.value.map((device) => device.device_id),
      { resetSelection: true }
    );
  } catch (error) {
    if (error !== "cancel") {
      logger.error("创建定向升级任务失败", error);
      ElMessage.error("创建升级任务失败，请稍后重试");
    }
  }
};

// 获取状态类型
const getStatusType = (status: string) => {
  const typeMap: Record<
    string,
    "success" | "warning" | "primary" | "danger" | "info"
  > = {
    latest: "success",
    up_to_date: "success",
    pending: "warning",
    outdated: "warning",
    upgrading: "primary",
    failed: "danger",
  };
  return typeMap[status] || "info";
};

// 获取状态文本
const getStatusText = (status: string) => {
  const textMap: Record<string, string> = {
    latest: "最新版本",
    up_to_date: "最新版本",
    pending: "待升级",
    outdated: "需要升级",
    upgrading: "升级中",
    failed: "升级失败",
  };
  return textMap[status] || "未知";
};

// 刷新固件列表
const refreshFirmware = () => {
  ElMessage.success("固件列表已刷新");
};

// 设置当前固件版本
const setActiveFirmware = async (firmware: {
  version: string;
  id: string;
  status: string;
}) => {
  try {
    const isCurrent = firmware.status === "latest";
    const message = isCurrent
      ? `确定要将版本 ${firmware.version} 设为历史版本吗？`
      : `确定要将版本 ${firmware.version} 设为当前版本吗？`;

    await ElMessageBox.confirm(message, "确认操作", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    });

    loading.value = true;

    // 调用后台API设置固件为当前版本
    const response = await DataService.setActiveFirmware(Number(firmware.id));

    if (response && response.data && response.data.code === 0) {
      ElMessage.success(
        isCurrent ? "固件版本已设为历史版本" : "固件版本已设为当前版本"
      );
      // 刷新固件列表以获取最新状态
      await fetchOTAData();
    } else {
      ElMessage.error(response?.data?.message || "设置失败");
    }
  } catch (error) {
    if (error !== "cancel") {
      console.error("设置固件为当前版本失败:", error);
      ElMessage.error("设置失败，请稍后重试");
    }
  } finally {
    loading.value = false;
  }
};

// 删除固件
const deleteFirmware = async (firmware: {
  version: string;
  id: string;
  status: string;
}) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除版本 ${firmware.version} 吗？`,
      "确认删除",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    loading.value = true;

    // 调用后台API删除固件
    const response = await DataService.deleteFirmware(Number(firmware.id));

    if (response && response.data && response.data.code === 0) {
      ElMessage.success("固件已删除");
      // 刷新固件列表
      await fetchOTAData();
    } else {
      ElMessage.error(response?.data?.message || "删除失败");
    }
  } catch (error) {
    if (error !== "cancel") {
      console.error("删除固件失败:", error);
      ElMessage.error("删除失败，请稍后重试");
    }
  } finally {
    loading.value = false;
  }
};

// 升级设备
const upgradeDevice = async (device: {
  deviceName: string;
  device_id: string;
  currentVersion?: string;
}) => {
  if (!selectedFirmware.value) {
    ElMessage.warning("请先在上方选择目标固件版本");
    return;
  }

  const targetVersion = selectedFirmware.value.version;
  const currentVersion = device.currentVersion || "";
  const cmp = compareVersion(currentVersion, targetVersion);
  if (cmp === 0) {
    ElMessage.info(
      `设备 ${
        device.deviceName || device.device_id
      } 当前已是版本 ${currentVersion}`
    );
    return;
  }

  const isDowngrade = cmp > 0;
  const confirmMessage = isDowngrade
    ? `当前版本为 ${currentVersion}，目标版本为 ${targetVersion} ，将执行降级操作。\n确定要将设备 ${
        device.deviceName || device.device_id
      } 从 ${currentVersion} 降级到 ${targetVersion} 吗？`
    : `确定要将设备 ${
        device.deviceName || device.device_id
      } 升级到版本 ${targetVersion} 吗？`;

  try {
    await ElMessageBox.confirm(confirmMessage, "确认升级", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    });

    await startUpgradeForDevices([device.device_id]);
  } catch (error) {
    if (error !== "cancel") {
      logger.error("单设备升级失败", error);
    }
  }
};

// 取消升级
const cancelUpgrade = async (device: {
  deviceName: string;
  device_id: string;
}) => {
  const deviceLabel = device.deviceName || device.device_id;
  try {
    await ElMessageBox.confirm(
      `确定要取消设备 ${deviceLabel} 的升级吗？`,
      "确认取消",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    loading.value = true;
    const response = await DataService.cancelOTADeviceUpgrade(device.device_id);

    if (response && response.data && response.data.code === 0) {
      ElMessage.success("升级已取消");
      await fetchOTAData();
    } else {
      ElMessage.error(response?.data?.message || "取消升级失败");
    }
  } catch (error) {
    if (error !== "cancel") {
      logger.error("取消升级失败", error);
      ElMessage.error("取消升级失败，请稍后重试");
    }
  } finally {
    loading.value = false;
  }
};

// 查看升级日志
const viewUpgradeLog = (device: { deviceName: string }) => {
  ElMessage.info(`查看设备 ${device.deviceName} 的升级日志`);
};

// 重置上传表单
const resetUploadForm = () => {
  uploadForm.value = {
    version: "",
    description: "",
    file: null,
  };
  fileList.value = [];
  if (uploadFormRef.value) {
    uploadFormRef.value.resetFields();
  }
};

// 处理取消上传
const handleCancelUpload = () => {
  showUploadDialog.value = false;
  resetUploadForm();
};

// 音频上传逻辑
const resetAudioUploadForm = () => {
  audioUploadForm.value = { audioType: "alarm", file: null };
  audioFileList.value = [];
};

const openAudioUploadDialog = (audioType?: string) => {
  if (audioType && audioTypeOptions.some((item) => item.value === audioType)) {
    audioUploadForm.value.audioType = audioType;
  }
  showAudioUploadDialog.value = true;
};

const handleAudioFileRemove = () => {
  audioUploadForm.value.file = null;
  audioFileList.value = [];
};

const handleAudioFileChange = (file: any) => {
  const raw = file.raw as File | undefined;
  if (!raw) {
    handleAudioFileRemove();
    return;
  }
  if (!raw.name.toLowerCase().endsWith(".mp3")) {
    ElMessage.error("仅支持 .mp3 文件");
    handleAudioFileRemove();
    return;
  }
  if (!ASCII_FILENAME_REGEX.test(raw.name)) {
    ElMessage.error("文件名仅允许 ASCII 字符，不能包含空格和中文");
    handleAudioFileRemove();
    return;
  }
  if (raw.size > 300 * 1024) {
    ElMessage.error("文件大小不得超过 300KB");
    handleAudioFileRemove();
    return;
  }
  audioUploadForm.value.file = raw;
  audioFileList.value = [file];
};

const cancelAudioUpload = () => {
  showAudioUploadDialog.value = false;
  resetAudioUploadForm();
};

const uploadAudioAsset = async () => {
  if (!audioUploadForm.value.audioType) {
    ElMessage.warning("请选择音频类型");
    return;
  }
  if (!audioUploadForm.value.file) {
    ElMessage.warning("请先选择 mp3 文件");
    return;
  }
  const formData = new FormData();
  formData.append("file", audioUploadForm.value.file);

  audioUploadLoading.value = true;
  try {
    const response = await DataService.uploadOTAAudio(
      audioUploadForm.value.audioType,
      formData
    );
    if (response && response.data && response.data.code === 0) {
      ElMessage.success("音频上传成功");
      showAudioUploadDialog.value = false;
      resetAudioUploadForm();
      await fetchAudioAssets();
    } else {
      ElMessage.error(response?.data?.message || "上传失败");
    }
  } catch (error) {
    logger.error("上传音频失败:", error);
    ElMessage.error("上传失败，请稍后重试");
  } finally {
    audioUploadLoading.value = false;
  }
};

// 处理文件变化
const handleFileChange = (file: any) => {
  uploadForm.value.file = file.raw;

  // 自动解析文件名中的版本号
  const fileName = file.name;
  console.log("上传文件:", fileName);

  // 匹配kangyang_x.x.x.bin格式
  const kangyangVersionMatch = fileName.match(
    /kangyang_([v]?\d+\.\d+(?:\.\d+)?)\.bin/i
  );
  if (kangyangVersionMatch) {
    let versionStr = kangyangVersionMatch[1];
    // 确保版本号格式正确
    if (!versionStr.startsWith("v")) {
      versionStr = `v${versionStr}`;
    }
    uploadForm.value.version = versionStr;
    console.log("从文件名解析版本号:", versionStr);
    ElMessage.success(`自动解析版本号: ${versionStr}`);
    return;
  }

  // 匹配其他常见的版本号格式
  const versionMatch = fileName.match(/[v]?(\d+\.\d+(?:\.\d+)?)/);
  if (versionMatch) {
    let versionStr = versionMatch[0];
    if (!versionStr.startsWith("v")) {
      versionStr = `v${versionStr}`;
    }
    uploadForm.value.version = versionStr;
    console.log("从文件名解析版本号:", versionStr);
    ElMessage.success(`自动解析版本号: ${versionStr}`);
    return;
  }

  // 如果没有解析到版本号，清空版本号字段
  uploadForm.value.version = "";
  console.log("未从文件名解析到版本号");
};

// 上传固件
const uploadFirmware = async () => {
  uploadFormRef.value.validate(async (valid: boolean) => {
    if (valid) {
      try {
        loading.value = true;

        // 创建FormData
        const formData = new FormData();
        if (uploadForm.value.file) {
          formData.append("file", uploadForm.value.file);
        }
        formData.append("description", uploadForm.value.description);
        formData.append("release_notes", ""); // 暂时为空
        formData.append("compatibility", "ESP32-S3"); // 默认兼容性

        // 调用API上传固件
        const response = await DataService.uploadFirmware(formData);

        if (response && response.data && response.data.code === 0) {
          ElMessage.success("固件上传成功");
          showUploadDialog.value = false;

          // 重置表单
          resetUploadForm();

          // 刷新固件列表
          await fetchOTAData();
        } else {
          ElMessage.error(response?.data?.message || "上传失败");
        }
      } catch (error) {
        console.error("上传固件失败:", error);
        ElMessage.error("上传失败，请稍后重试");
      } finally {
        loading.value = false;
      }
    }
  });
};

// 处理数据源变化
const handleDataSourceChange = () => {
  logger.info("🔄 检测到数据源变化，重新加载OTA数据");
  fetchOTAData();
};

// 搜索功能
const handleSearch = () => {
  fetchOTAData();
};

// 刷新数据
const refreshData = () => {
  logger.info("🔄 手动刷新OTA数据");
  fetchOTAData();
};

onMounted(() => {
  fetchOTAData();
  fetchAudioAssets(); // 添加这行，页面加载时查询音频资源

  // 监听数据源变化事件
  window.addEventListener("dataSourceChanged", handleDataSourceChange);
});

onUnmounted(() => {
  window.removeEventListener("dataSourceChanged", handleDataSourceChange);
  stopOtaPolling();
  cancelKeywordDebounce();
});

watch(firmwareList, (list) => {
  if (
    selectedFirmwareId.value !== null &&
    !list.some((fw) => Number(fw.id) === Number(selectedFirmwareId.value))
  ) {
    selectedFirmwareId.value = null;
  }
});
</script>

<style scoped>
.ota-management {
  height: 100vh;
  padding: 20px;
  background: #f5f5f5;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.firmware-section,
.device-upgrade-section {
  margin-bottom: 20px;
}

.section-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-filters {
  display: flex;
  gap: 12px;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.table-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.upload-demo {
  width: 100%;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.selected-firmware-row td {
  background-color: #ecf5ff !important;
}

/* 上传固件对话框样式优化 */
:deep(.el-form-item__label) {
  text-align: right;
  padding-right: 12px;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

:deep(.el-upload__text) {
  margin-top: 8px;
}

:deep(.el-upload__tip) {
  margin-top: 8px;
  color: #999;
  font-size: 12px;
}
</style>
